import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A ground-mounted solar array: rows of tilted photovoltaic panels on steel
// legs over a gravel pad, every panel facing the same way like a real solar
// farm. Each panel is built as one coplanar unit on a plane tilted about X so
// the frame, cells and seams stay flush.
const CELL = "#244a8c";
const GRID = "#3a64a8";
const FRAME = "#c2c6cd";
const RAIL = "#6b7079";
const PAD = "#7f7a70";

const TILT = 0.62; // all panels share this tilt (face up and toward +Z)

// One PV panel centred at (cx,cy,cz), `w` by `len`, tilted about X.
function pvPanel(cx: number, cy: number, cz: number, w: number, len: number): Part[] {
  const c = Math.cos(TILT), s = Math.sin(TILT);
  const P = (lx: number, ly: number, lz: number): Vec3 => [cx + lx, cy + ly * c - lz * s, cz + ly * s + lz * c];
  const rot: Vec3 = [TILT, 0, 0];
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(w, 0.028, len), color: FRAME, position: P(0, 0, 0), rotation: rot },
    { geometry: new THREE.BoxGeometry(w - 0.05, 0.022, len - 0.05), color: CELL, position: P(0, 0.016, 0), rotation: rot },
  ];
  for (const lz of [-len / 4, len / 4]) {
    parts.push({ geometry: new THREE.BoxGeometry(w - 0.05, 0.026, 0.012), color: GRID, position: P(0, 0.028, lz), rotation: rot });
  }
  parts.push({ geometry: new THREE.BoxGeometry(0.012, 0.026, len - 0.05), color: GRID, position: P(0, 0.028, 0), rotation: rot });
  return parts;
}

function panelRow(z: number): Part[] {
  const out: Part[] = [];
  for (const x of [-0.52, 0, 0.52]) {
    // Back leg (taller) and front leg (shorter) per panel.
    out.push({ geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.34, 6), color: RAIL, position: [x, 0.2, z - 0.14] });
    out.push({ geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.18, 6), color: RAIL, position: [x, 0.12, z + 0.13] });
    out.push(...pvPanel(x, 0.34, z, 0.5, 0.5));
  }
  return out;
}

export const solarArray = defineModel({
  id: "solar-array",
  batchId: "electric",
  name: "Solar array",
  tags: ["building", "electric", "power", "energy", "solar", "renewable"],
  summary: "Rows of photovoltaic panels on a pad, like a small solar farm.",
  footprint: 0.9,
  parts(): Part[] {
    return [
      { geometry: new THREE.BoxGeometry(1.7, 0.06, 1.5), color: PAD, position: [0, 0.03, 0] },
      ...panelRow(-0.4),
      ...panelRow(0.32),
    ];
  },
  animations: [],
});
